import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/auth';
import config from "../config";
import ListPanel from './ListPanel';
import PoiCardLite from './poicardlite';
import CategoryFilter from './CategoryFilter';
import { calculateDistance } from './utils';
import LocalIcon from './LocalIcon';
import 'leaflet/dist/leaflet.css';
import { getOptimizedPoiImage } from '../utils/imageUtils';

const NEARBY_FILTER = "all";
const ITALY_CENTER = [41.9028, 12.4964];
const DEFAULT_ZOOM = 6;
const PADDING = 24;
const VERTICAL_OFFSET = -120;
const ANIMATION_DELAY = 100;

const calculateMarkerSize = (zoom) => {
  const maxSize = { width: 50, height: 60 };
  if (zoom >= 15) return {
    size: maxSize,
    anchor: { x: maxSize.width / 2, y: maxSize.height }
  };
  const zoomDiff = 15 - zoom;
  const reductionFactor = Math.max(0.3, 1 - (zoomDiff * 0.04));
  const size = {
    width: Math.round(maxSize.width * reductionFactor),
    height: Math.round(maxSize.height * reductionFactor)
  };
  return {
    size,
    anchor: { x: Math.round(size.width / 2), y: size.height }
  };
};

const MapList = () => {
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [visiblePois, setVisiblePois] = useState([]);
  const [mapBounds, setMapBounds] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [initialPoiDetailLoad, setInitialPoiDetailLoad] = useState(true);
  const [cardState, setCardState] = useState('hidden');
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [isUserMarkerSelected, setIsUserMarkerSelected] = useState(false);
  const [pois, setPois] = useState([]);
  const [taxonomyData, setTaxonomyData] = useState({});
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isListPanelOpen, setIsListPanelOpen] = useState(false);
  const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);
  const mapRef = useRef(null);
  const sliderRef = useRef(null);
  const loadedTaxonomyIds = useRef(new Set());
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedCategories, setSelectedCategories] = useState(new Set());

  const taxonomyCache = useRef({});

  const filteredPois = useMemo(() => {
    if (!selectedCategories || selectedCategories.size === 0) return pois;
     
    // Converti selectedCategories in un Set di stringhe una sola volta
    const categoriesSet = new Set([...selectedCategories].map(String));
     
    return pois.filter(poi => {
      const poiCategoryId = poi.field_tipologia_poi?.[0]?.target_id;
      return poiCategoryId ? categoriesSet.has(String(poiCategoryId)) : false;
    });
  }, [pois, selectedCategories]);

  const handleListButtonClick = () => {
    setIsListPanelOpen(true);
    setSelectedPOI(null);
    setVisiblePois([]);
    setCardState('hidden');
  };

  const handleCategoryChange = useCallback((categories) => {
    if (categories instanceof Set) {
      setSelectedCategories(new Set(categories));
    } else if (Array.isArray(categories)) {
      setSelectedCategories(new Set(categories.map(String)));
    } else {
      setSelectedCategories(new Set());
    }
  }, []);

  const createUserIcon = useCallback((isSelected = false) => {
    if (!mapRef.current) return null;
    const zoom = mapRef.current.getZoom();
    const markerDimensions = calculateMarkerSize(zoom);
    const size = isSelected ? {
      width: Math.round(markerDimensions.size.width * 2),
      height: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.size;
    const anchor = isSelected ? {
      x: Math.round(markerDimensions.size.width * 2 / 2),
      y: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.anchor;
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 719.4 842"><circle cx="362.7" cy="360.7" r="317.2" fill="#FF5555"/><circle cx="362.7" cy="360.7" r="317.2" fill="none" stroke="#FF3333" stroke-width="60"/><polygon points="359.7,842 436.1,696.4 283.2,696.4" fill="#FF3333"/><path d="M362.7 260 q-25 0-42.5-17.5 t-17.5-42.5 q0-25 17.5-42.5 t42.5-17.5 q25 0 42.5 17.5 t17.5 42.5 q0 25-17.5 42.5 t-42.5 17.5 Z" fill="#FFFFFF"/><path d="M302.7 550 v-140 h-40 v-120 q0-25 17.5-42.5 t42.5-17.5 h80 q25 0 42.5 17.5 t17.5 42.5 v120 h-40 v140 Z" fill="#FFFFFF" transform="translate(0, 40)"/></svg>`;
    const CustomIcon = L.Icon.extend({
      createIcon: function(oldIcon) {
        const div = (oldIcon && oldIcon.tagName === 'DIV') ? oldIcon : document.createElement('div');
        div.innerHTML = this.options.iconUrl;
        this._setIconStyles(div, 'icon');
        return div;
      }
    });
    return new CustomIcon({
      iconUrl: svgString,
      iconSize: [size.width, size.height],
      iconAnchor: [anchor.x, anchor.y],
      className: 'map-marker user-location-marker'
    });
  }, []);

  const loadTaxonomies = useCallback(async (pois) => {
    try {
      // Prima controlla se c'è già una cache in localStorage 
      // (ma solo al primo caricamento e considerando la lingua attuale)
      const cacheKey = `maplist_taxonomy_cache_${selectedLang}`;
      if (Object.keys(taxonomyCache.current).length === 0) {
        try {
          const cachedData = localStorage.getItem(cacheKey);
          if (cachedData) {
            const parsed = JSON.parse(cachedData);
            taxonomyCache.current = parsed;
            
            // Imposta subito i dati dalla cache
            Object.keys(parsed).forEach(id => {
              loadedTaxonomyIds.current.add(id);
            });
            setTaxonomyData(prev => ({...prev, ...parsed}));
          }
        } catch (e) {
          // Ignora errori di parsing
        }
      }
      
      const taxonomyRequests = pois.reduce((acc, poi) => {
        const taxonomyId = poi.field_tipologia_poi?.[0]?.target_id;
        
        // Usa la cache prima se disponibile
        if (taxonomyId && taxonomyCache.current[taxonomyId]) {
          if (!loadedTaxonomyIds.current.has(taxonomyId)) {
            setTaxonomyData(prev => ({
              ...prev,
              [taxonomyId]: taxonomyCache.current[taxonomyId]
            }));
            loadedTaxonomyIds.current.add(taxonomyId);
          }
          return acc;
        }
        
        if (taxonomyId && !loadedTaxonomyIds.current.has(taxonomyId)) {
          acc.push(
            authService.get(config.API_ENDPOINTS.getTaxonomyTerm(taxonomyId, selectedLang))
              .then(response => {
                if (!response || !response.tid?.[0]?.value) {
                  return null;
                }

                const taxonomyObject = {
                  title: response.name?.[0]?.value || 'Senza nome',
                  icon: response.field_icona?.[0]?.url || null,
                  pin: response.field_pin?.[0]?.url || null,
                  color: response.field_colore?.[0]?.color || '#1E88E5'
                };
                
                // Aggiorna sia lo stato che la cache
                taxonomyCache.current[taxonomyId] = taxonomyObject;
                setTaxonomyData(prev => {
                  const updated = {
                    ...prev,
                    [taxonomyId]: taxonomyObject
                  };
                  loadedTaxonomyIds.current.add(taxonomyId);
                  
                  // Salva la cache aggiornata in localStorage con la lingua specifica
                  localStorage.setItem(cacheKey, 
                    JSON.stringify(taxonomyCache.current));
                    
                  return updated;
                });
              })
              .catch(error => {
                loadedTaxonomyIds.current.delete(taxonomyId);
              })
          );
        }
        return acc;
      }, []);
    
      if (taxonomyRequests.length > 0) {
        await Promise.all(taxonomyRequests);
      }
    } catch (error) {
      // Gestione errori
    }
  }, [selectedLang]);

  const createIcon = useCallback((poi, isSelected) => {
    if (!mapRef.current) return null;
    const zoom = mapRef.current.getZoom();
    const markerDimensions = calculateMarkerSize(zoom);
    const uniqueId = `poi-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const taxonomyId = poi.field_tipologia_poi?.[0]?.target_id;
    const taxonomy = taxonomyId ? taxonomyData[taxonomyId] : null;
    
    // Usa il colore dalla tassonomia o il colore di default
    const color = taxonomy?.color || '#1E88E5';

    let poiImage = getOptimizedPoiImage(poi, 'thumbnail');

    const size = isSelected ? {
      width: Math.round(markerDimensions.size.width * 2),
      height: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.size;

    const anchor = isSelected ? {
      x: Math.round(markerDimensions.size.width * 2 / 2),
      y: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.anchor;

    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 719.4 842">
        <defs>
          <pattern id="imagePattern-${uniqueId}" patternUnits="userSpaceOnUse" width="634.4" height="634.4" x="45.5" y="43.5">
            <image x="30" y="-50" width="80%" height="80%" href="${poiImage || ''}" preserveAspectRatio="xMidYMid slice"/>
          </pattern>
        </defs>
        <circle cx="362.7" cy="360.7" r="317.2" fill="url(#imagePattern-${uniqueId})"/>
        <circle cx="362.7" cy="360.7" r="317.2" fill="none" stroke="${color}" stroke-width="60"/>
        <polygon points="359.7,842 436.1,696.4 283.2,696.4" fill="${color}"/>
      </svg>`;

    const CustomIcon = L.Icon.extend({
      createIcon: function(oldIcon) {
        const div = (oldIcon && oldIcon.tagName === 'DIV') ? oldIcon : document.createElement('div');
        div.innerHTML = svgString;
        this._setIconStyles(div, 'icon');
        return div;
      }
    });

    return new CustomIcon({
      iconSize: [size.width, size.height],
      iconAnchor: [anchor.x, anchor.y],
      popupAnchor: [0, -20],
      className: 'map-marker'
    });
  }, [taxonomyData]);

  const handleScroll = useCallback(() => {
    if (!sliderRef.current) return;
    
    const slider = sliderRef.current;
    const scrollPosition = slider.scrollLeft;
    const cardElement = slider.querySelector('.card-container');
    if (!cardElement) return;
    
    const cardWidth = cardElement.getBoundingClientRect().width;
    const newIndex = Math.round(scrollPosition / cardWidth);
    
    setCanScrollLeft(scrollPosition > 0);
    setCanScrollRight(scrollPosition < slider.scrollWidth - slider.clientWidth - 1);
  
    if (newIndex >= 0 && newIndex < visiblePois.length && newIndex !== activeCardIndex) {
      const newSelectedPoi = visiblePois[newIndex];
      // Aggiorniamo subito gli stati UI
      setActiveCardIndex(newIndex);
      setSelectedPOI(newSelectedPoi);
      
      // Gestiamo separatamente l'animazione della mappa
      if (mapRef.current && newSelectedPoi?.field_geolocalizzazione?.[0]) {
        const { lat, lng } = newSelectedPoi.field_geolocalizzazione[0];
        if (isFinite(lat) && isFinite(lng)) {
          const map = mapRef.current;
          requestAnimationFrame(() => {
            const pixelCenter = map.latLngToContainerPoint([lat, lng]);
            const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
            const newCenter = map.containerPointToLatLng(newPixelCenter);
            map.setView(newCenter, map.getZoom(), { 
              animate: true,
              duration: 0.5
            });
          });
        }
      }
    }
  }, [activeCardIndex, visiblePois]);

  const scrollToIndex = useCallback((index) => {
    if (!sliderRef.current || index < 0 || index >= visiblePois.length) return;
    const cards = sliderRef.current.querySelectorAll('.card-container');
    if (cards[index]) {
      cards[index].scrollIntoView({ 
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  }, [visiblePois.length]);

  const handleTouchStart = useCallback((e) => {
    if (!sliderRef.current) return;
    const slider = sliderRef.current;
    slider.dataset.touchStartX = e.touches[0].clientX;
    slider.dataset.scrollStartX = slider.scrollLeft;
  }, []);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!sliderRef.current) return;
    const slider = sliderRef.current;
    const touchEndX = e.changedTouches[0].clientX;
    const touchStartX = parseFloat(slider.dataset.touchStartX);
    const diff = touchEndX - touchStartX;
    
    // Riduco la sensibilità e aggiungo un meccanismo di blocco più soft
    if (Math.abs(diff) > 50) {
      slider.dataset.isScrolling = 'true';
      
      if (diff > 0 && activeCardIndex > 0) {
        scrollToIndex(activeCardIndex - 1);
      } else if (diff < 0 && activeCardIndex < visiblePois.length - 1) {
        scrollToIndex(activeCardIndex + 1);
      }
      
      // Resetta il flag dopo un intervallo più breve
      setTimeout(() => {
        if (slider) {
          delete slider.dataset.isScrolling;
        }
      }, 200);
    }
  }, [activeCardIndex, visiblePois.length, scrollToIndex]);


  const handleMarkerClick = useCallback((poi) => {
    if (!mapRef.current || !poi.field_geolocalizzazione?.[0]) return;
    
    // Blocchiamo qualsiasi operazione se c'è una transizione in corso
    if (cardState === 'exiting') return;
    
    // Teniamo una referenza al POI che vogliamo visualizzare
    const targetPoi = poi;
    
    // Calcola i POI nelle vicinanze
    const filteredNearbyPois = filteredPois
      .filter(p => {
        if (!p.field_geolocalizzazione?.[0]) return false;
        const { lat: pLat, lng: pLng } = p.field_geolocalizzazione[0];
        if (!isFinite(pLat) || !isFinite(pLng)) return false;
        const distance = calculateDistance(
          targetPoi.field_geolocalizzazione[0].lat,
          targetPoi.field_geolocalizzazione[0].lng,
          pLat,
          pLng
        );
        return distance <= 10;
      })
      .map(p => {
        const distance = calculateDistance(
          targetPoi.field_geolocalizzazione[0].lat,
          targetPoi.field_geolocalizzazione[0].lng,
          p.field_geolocalizzazione[0].lat,
          p.field_geolocalizzazione[0].lng
        );
        return { ...p, distance };
      })
      .sort((a, b) => a.distance - b.distance);
    
    // Soluzione più radicale: rimuovi tutto e aspetta prima di mostrare i nuovi pannelli
    if (cardState === 'visible') {
      // Rimuovi completamente i pannelli esistenti
      setCardState('hidden');
      
      // Pulizia completa dello stato
      setSelectedPOI(null);
      setVisiblePois([]);
      setActiveCardIndex(0);
      
      // Resetta manualmente lo scroll prima di qualsiasi operazione
      if (sliderRef.current) {
        sliderRef.current.scrollLeft = 0;
        delete sliderRef.current.dataset.isScrolling;
      }
      
      // Attendi che il DOM si aggiorni completamente prima di procedere
      setTimeout(() => {
        // Ora aggiungi i nuovi pannelli
        setSelectedPOI(targetPoi);
        setVisiblePois(filteredNearbyPois);
        setActiveCardIndex(0);
        
        // Ora mostra le card
        setCardState('visible');
        
        // Sposta la mappa
        if (mapRef.current) {
          const map = mapRef.current;
          const { lat, lng } = targetPoi.field_geolocalizzazione[0];
          const pixelCenter = map.latLngToContainerPoint([lat, lng]);
          const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
          const newCenter = map.containerPointToLatLng(newPixelCenter);
          map.setView(newCenter, map.getZoom(), { animate: true });
        }
      }, 50); // Un timeout minimo per garantire che il DOM sia aggiornato
    } else {
      // Se le card sono già nascoste, procedi normalmente
      setSelectedPOI(targetPoi);
      setVisiblePois(filteredNearbyPois);
      setActiveCardIndex(0);
      
      // Resetta lo scroll 
      if (sliderRef.current) {
        sliderRef.current.scrollLeft = 0;
        delete sliderRef.current.dataset.isScrolling;
      }
      
      setCardState('entering');
      
      // Aggiungi un timeout per passare a visible dopo aver impostato entering
      setTimeout(() => {
        setCardState('visible');
        
        // Sposta la mappa
        if (mapRef.current) {
          const map = mapRef.current;
          const { lat, lng } = targetPoi.field_geolocalizzazione[0];
          const pixelCenter = map.latLngToContainerPoint([lat, lng]);
          const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
          const newCenter = map.containerPointToLatLng(newPixelCenter);
          map.setView(newCenter, map.getZoom(), { animate: true });
        }
      }, 0);
    }
  }, [cardState, filteredPois]);

  const handleClosePanel = useCallback(() => {
    const slider = sliderRef.current;
    
    // Rimuovi il controllo condizionale che blocca la chiusura
    setCardState('exiting');
    setTimeout(() => {
      setActiveCardIndex(0);
      setSelectedPOI(null);
      setVisiblePois([]);
      setCardState('hidden');
      setCanScrollLeft(false);
      setCanScrollRight(false);
      
      // Rimuovi eventuali flag di scorrimento
      if (slider) {
        delete slider.dataset.isScrolling;
      }
    }, ANIMATION_DELAY);
  }, []);
  

  const calculateFilteredPois = useCallback((selectedPOI, allPois, maxDistance) => {
    const categorizedPois = selectedCategories.size > 0
      ? allPois.filter(poi => {
          const poiCategoryId = poi.field_tipologia_poi?.[0]?.target_id?.toString();
          return selectedCategories.has(poiCategoryId);
        })
      : allPois;
  
    if (!selectedPOI?.field_geolocalizzazione?.[0] || !categorizedPois.length) return [];
    
    const { lat: selectedLat, lng: selectedLng } = selectedPOI.field_geolocalizzazione[0];
    if (!isFinite(selectedLat) || !isFinite(selectedLng)) return [];
  
    const poisWithDistance = categorizedPois
      .map(poi => {
        if (!poi.field_geolocalizzazione?.[0]) return null;
        const { lat, lng } = poi.field_geolocalizzazione[0];
        if (!isFinite(lat) || !isFinite(lng)) return null;
        const distance = calculateDistance(selectedLat, selectedLng, lat, lng);
        return { ...poi, distance };
      })
      .filter(Boolean);
  
    const filtered = maxDistance === "all" 
      ? poisWithDistance 
      : poisWithDistance.filter(poi => poi.distance <= parseFloat(maxDistance));
  
    return filtered.sort((a, b) => a.distance - b.distance);
  }, [selectedCategories]);

  const MapController = React.memo(() => {
    const map = useMap();
  
    useEffect(() => {
      mapRef.current = map;
      
      const handleZoomAnim = (e) => setMapZoom(e.zoom);
      const handleZoom = () => setMapZoom(map.getZoom());
      
      map.on('zoomanim', handleZoomAnim);
      map.on('zoom', handleZoom);
      
      if (isInitialLoad && mapBounds && !location.state?.selectedPOI) {
        if (mapBounds.isValid()) {
          map.fitBounds(mapBounds, { 
            padding: [PADDING, PADDING],
            maxZoom: 19
          });
        }
        setIsInitialLoad(false);
      }
      if (location.state?.selectedPOI && selectedPOI && initialPoiDetailLoad) {
        const { lat, lng } = selectedPOI.field_geolocalizzazione[0];
        
        if (isFinite(lat) && isFinite(lng)) {
          map.setView([lat, lng], 17, { animate: false });
          
          setTimeout(() => {
            const pixelCenter = map.latLngToContainerPoint([lat, lng]);
            const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
            const newCenter = map.containerPointToLatLng(newPixelCenter);
            
            if (isFinite(newCenter.lat) && isFinite(newCenter.lng)) {
              map.setView(newCenter, 17, { 
                animate: true,
                duration: 0.5
              });
            }
          }, ANIMATION_DELAY);
        }
        
        setInitialPoiDetailLoad(false);
        setCardState('visible');
      }

      return () => {
        map.off('zoomanim', handleZoomAnim);
        map.off('zoom', handleZoom);
      };
    }, [map]);

    return null;
  });

  const fetchUserLocation = useCallback(() => {
    if (!navigator.geolocation) return null;

    const options = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 30000
    };

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (isFinite(latitude) && isFinite(longitude)) {
          setUserLocation({ latitude, longitude });
        }
      },
      (error) => {
      },
      options
    );

    return watchId;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await authService.get(config.API_ENDPOINTS.getPOIList(selectedLang));
        const validPois = response.filter(poi => {
          const coords = poi.field_geolocalizzazione?.[0];
          return coords && isFinite(coords.lat) && isFinite(coords.lng);
        });
        
        setPois(validPois);

        // Carica le tassonomie usando il metodo loadTaxonomies
        await loadTaxonomies(validPois);
        
        setLoading(false);
      } catch (error) {
        setError('Errore durante il caricamento dei dati');
        setLoading(false);
      }
    };

    const watchId = fetchUserLocation();

    if (selectedLang) {
      fetchData();
    }

    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [selectedLang, fetchUserLocation, loadTaxonomies]);

  useEffect(() => {
    if (visiblePois.length > 0) {
      visiblePois.forEach(poi => {
        //const img = poi.field_immagini?.[0]?.url;
        const img = getOptimizedPoiImage(poi, 'thumbnail');
        if (img) {
          const image = new Image();
          image.src = img;
        }
      });
    }
  }, [visiblePois]);

  useEffect(() => {
    // Precaricare le icone delle categorie più comuni
    Object.values(taxonomyData).forEach(taxonomy => {
      if (taxonomy.icon) {
        const img = new Image();
        img.src = taxonomy.icon;
      }
    });
  }, [taxonomyData]);

  useEffect(() => {
    if (pois.length > 0) {
      const validCoordinates = pois
        .map(poi => poi.field_geolocalizzazione?.[0])
        .filter(coords => coords && isFinite(coords.lat) && isFinite(coords.lng))
        .map(({ lat, lng }) => [lat, lng]);

      if (validCoordinates.length > 0) {
        const bounds = L.latLngBounds(validCoordinates);
        setMapBounds(bounds);
      }

      if (location.state?.selectedPOI) {
        const selectedPoi = location.state.selectedPOI;
        if (selectedPoi.field_geolocalizzazione?.[0]) {
          setSelectedPOI(selectedPoi);
          const filtered = calculateFilteredPois(selectedPoi, pois, NEARBY_FILTER);
          setVisiblePois(filtered);
          setCardState('visible');
        }
      }
    }
  }, [pois, location.state, calculateFilteredPois]);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    handleScroll();
    slider.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      slider.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll]);

  // Reset della cache quando cambia la lingua
  useEffect(() => {
    // Reset della cache in memoria e dei dati caricati quando cambia la lingua
    taxonomyCache.current = {};
    loadedTaxonomyIds.current.clear();
    setTaxonomyData({});
  }, [selectedLang]);

  if (error) {
    return <div className="alert alert-danger m-3">{error}</div>;
  }

  return (
    <div style={{ height: 'calc(100dvh - 140px)', scrollSnapAlign: 'start', position: 'relative' }}>
      <MapContainer
        center={ITALY_CENTER}
        zoom={DEFAULT_ZOOM}
        id="map"
        zoomControl={false}
      >
        <MapController />
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          minZoom={5}
          maxZoom={19}
        />

        

<CategoryFilter 
  taxonomyData={taxonomyData}
  onFilterChange={handleCategoryChange}
  selectedCategories={new Set([...selectedCategories])} // Forziamo un nuovo Set per assicurarci che CategoryFilter riceva l'update
  onPanelOpen={handleClosePanel}
  style={{
    position: 'absolute',
    top: '6px',
    left: '8px',
    zIndex: 1000
  }}
/>

        {filteredPois.map((poi) => {
          const latLng = poi.field_geolocalizzazione?.[0];
          if (!latLng || !isFinite(latLng.lat) || !isFinite(latLng.lng)) return null;
         
          const { lat, lng } = latLng;
          const visibleIndex = visiblePois.findIndex(vPoi => vPoi.nid[0].value === poi.nid[0].value);
          const isSelected = selectedPOI && selectedPOI.nid[0].value === poi.nid[0].value;
          const isVisible = visibleIndex !== -1;
                   
          return (
            <Marker
              key={poi.nid[0].value}
              position={[lat, lng]}
              icon={createIcon(poi, isSelected)}
              eventHandlers={{
                click: () => handleMarkerClick(poi),
              }}
              className={`map-marker ${isVisible ? 'visible' : ''} ${isSelected ? 'selected' : ''}`}
              zIndexOffset={isSelected ? 1000 : isVisible ? 500 : 0}
            />
          );
        })}

        {userLocation && isFinite(userLocation.latitude) && isFinite(userLocation.longitude) && (
          <Marker
            position={[userLocation.latitude, userLocation.longitude]}
            icon={createUserIcon(isUserMarkerSelected)}
            interactive={false}
          />
        )}

        <button
          aria-label="La mia posizione"
          className="btn bg-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm opacity-75"
          style={{position: 'absolute', top: '6px', right: '8px', zIndex: 1000, width: '40px', height: '40px'}}
          onClick={() => {
            if (userLocation && mapRef.current) {
              const map = mapRef.current;
              const { latitude, longitude } = userLocation;
              
              setIsUserMarkerSelected(true);
              setTimeout(() => setIsUserMarkerSelected(false), 2000);
              
              const pixelCenter = map.latLngToContainerPoint([latitude, longitude]);
              const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
              const newCenter = map.containerPointToLatLng(newPixelCenter);
              map.setView(newCenter, map.getZoom(), { animate: true });
              
              handleClosePanel();
              setSelectedPOI(null);
            }
          }}
        >
          <LocalIcon icon="my_location" />
        </button>

        <button
          aria-label="Lista"
          className="btn bg-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm opacity-75"
          style={{position: 'absolute', top: '54px', right: '8px', zIndex: 1000, width: '40px', height: '40px'}}
          onClick={handleListButtonClick}
        >
          <LocalIcon icon="list" />
        </button>
      </MapContainer>

      {selectedPOI && (
        <div className={`map-card ${cardState}`}>
          <div className="position-relative">
            {canScrollLeft && (
              <button
                className="btn bg-white border rounded-circle btn-scroll opacity-75 shadow-sm position-absolute start-0 top-50 translate-middle-y d-flex align-items-center justify-content-center"
                onClick={() => scrollToIndex(activeCardIndex - 1)}
                aria-label="Previous"
              >
                <LocalIcon icon="chevron_left" style={{ fontSize: '20px' }} />
              </button>
            )}
            
            {canScrollRight && (
              <button
                className="btn bg-white border rounded-circle btn-scroll opacity-75 shadow-sm position-absolute end-0 top-50 translate-middle-y d-flex align-items-center justify-content-center"
                onClick={() => scrollToIndex(activeCardIndex + 1)}
                aria-label="Next"
              >
                <LocalIcon icon="chevron_right" style={{ fontSize: '20px' }} />
              </button>
            )}

            <div 
              ref={sliderRef}
              className="row flex-nowrap carousel-container"
              style={{
                scrollbarWidth: 'none',
                position: 'relative',
                padding: '0 8px',
                scrollSnapType: 'x mandatory',
                overflowX: 'hidden',
                touchAction: 'none'
              }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {visiblePois.map(poi => (
                <div 
                  key={poi.nid[0].value} 
                  className="card-container card-containerlite px-1"
                  style={{
                    scrollSnapAlign: 'center',
                    scrollSnapStop: 'always',
                    flexShrink: 0
                  }}>
                  <PoiCardLite 
                    poi={poi}
                    taxonomyData={taxonomyData}
                    distance={
                      userLocation && poi.field_geolocalizzazione?.[0] ?
                      `${calculateDistance(
                        userLocation.latitude,
                        userLocation.longitude,
                        poi.field_geolocalizzazione[0].lat,
                        poi.field_geolocalizzazione[0].lng
                      ).toFixed(1)} km` :
                      '...'
                    }
                    onClose={handleClosePanel}
                    handleNavigate={() => navigate(`/poi/${poi.nid[0].value}`, {
                      state: {
                        selectedPOI: poi,           // Il POI già caricato
                        allPois: pois,              // Tutti i POI già caricati
                        taxonomyData: taxonomyData  // Dati tassonomia già caricati
                      }
                    })}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

<ListPanel
  isOpen={isListPanelOpen}
  onClose={() => setIsListPanelOpen(false)}
  pois={pois}
  taxonomyData={taxonomyData}
  userLocation={userLocation}
  selectedCategories={selectedCategories}
  setSelectedCategories={handleCategoryChange}  // Usiamo handleCategoryChange anche qui
/>
    </div>
  );
};

export default MapList;